import React from 'react'
import { isEmpty } from 'lodash'
import Table from 'antd/lib/table'
import Switch from 'antd/lib/switch'
import Tooltip from 'antd/lib/tooltip'
import { QuestionCircleOutlined } from '@ant-design/icons'
import SnoozeButton from './SnoozeButton'
import { getAllOutOfStockGroups } from './utils'
import { StoreModifier } from '../types'
import styled from '@emotion/styled'
import { isAdmin, isManager, useSession } from 'packages/@slerp/accounts'
import { AvailabilityAction } from './types'
import { Cell } from './styles'
import { CommonTag } from 'components/Common'

interface ModifiersInventoryTableProps {
  storeModifiers: StoreModifier[]
  isPreorder?: boolean
  isLoading: boolean
  onToggle: (action: AvailabilityAction, id: string, name: string) => void
  processingModifierStockToggle: string[]
  isModifierTogglingStockState: boolean
  snoozingStoreModifier: boolean
  unsnoozingStoreModifier: boolean
  selectedRowKeys: string[]
  onSelectedRowKeysChange: (keys: any) => void
}

const ModifiersInventoryTable = ({
  storeModifiers,
  isPreorder,
  isLoading,
  onToggle,
  processingModifierStockToggle,
  isModifierTogglingStockState,
  snoozingStoreModifier,
  unsnoozingStoreModifier,
  selectedRowKeys,
  onSelectedRowKeysChange
}: ModifiersInventoryTableProps) => {
  const { user } = useSession()
  const canManage = isAdmin(user) || isManager(user)

  const allOutOfStockGroups = React.useMemo(
    () =>
      getAllOutOfStockGroups(
        storeModifiers.map((sm) => ({
          in_stock: sm.in_stock,
          availability_group: sm.modifier?.availability_group
        }))
      ),
    [storeModifiers]
  )

  const STORE_MODIFIER_COLUMNS = [
    {
      title: 'Modifier Name',
      dataIndex: ['modifier', 'name'],
      key: 'modifier_name'
    },
    {
      title: 'SKU',
      dataIndex: ['modifier', 'sku'],
      key: 'modifier_sku',
      render: (sku: string) => <WrappText>{sku}</WrappText>
    },
    {
      title: 'Availability group',
      key: 'availability_group',
      sorter: (a: StoreModifier, b: StoreModifier) =>
        (a.modifier?.availability_group || '').localeCompare(
          b.modifier?.availability_group || ''
        ),
      render: (record: StoreModifier) => {
        const group = record.modifier?.availability_group
        return (
          group && (
            <Cell>
              <CommonTag
                style={{ margin: '0 auto' }}
                type={allOutOfStockGroups.has(group) ? 'active' : 'default'}
              >
                {group}
              </CommonTag>
            </Cell>
          )
        )
      }
    },
    {
      title: 'In Stock',
      dataIndex: 'in_stock',
      key: 'in_stock',
      width: '100px',
      render: (
        inStock: boolean,
        { id, modifier_id, unsnooze_at, modifier }: StoreModifier
      ) => {
        const loading = processingModifierStockToggle.includes(modifier_id)
        return (
          <Switch
            loading={loading}
            checked={inStock}
            disabled={
              !canManage ||
              isModifierTogglingStockState ||
              !isEmpty(unsnooze_at)
            }
            onChange={(value: boolean) =>
              onToggle(
                value
                  ? AvailabilityAction.IN_STOCK
                  : AvailabilityAction.OUT_OF_STOCK,
                id,
                modifier.name
              )
            }
            data-testid={`modifiers-${modifier.name}-in-stock-switch`}
          />
        )
      }
    }
  ]

  const MODIFIER_SNOOZE_COLUMN = canManage
    ? [
        {
          title: (
            <>
              <span>Snooze</span>
              <Tooltip
                title={
                  <span>
                    Modifier snooze allows you to mark a product temporarily out
                    of stock. For more information read{' '}
                    <a
                      href='https://support.slerp.com/en_us/preview/article/modifier-snooze-Bkjm7RHn5'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      here
                    </a>
                    .
                  </span>
                }
              >
                <QuestionCircleOutlined className='_ml-8 _mr-8' />
              </Tooltip>
            </>
          ),
          dataIndex: 'snooze',
          key: 'snooze',
          width: 240,
          render: (
            val: undefined | null,
            { id, modifier, unsnooze_at }: StoreModifier
          ) => {
            return (
              <SnoozeButton
                key={id}
                itemId={id}
                name={modifier.name}
                unsnoozeAt={unsnooze_at}
                onClickHandler={onToggle}
                loading={snoozingStoreModifier || unsnoozingStoreModifier}
                disabled={snoozingStoreModifier || unsnoozingStoreModifier}
                dataTestId='inventory-dashboard-snooze-modifier'
              />
            )
          }
        }
      ]
    : []

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectedRowKeysChange
  }

  return (
    <Table
      loading={isLoading}
      data-testid='inventory-dashboard-modifiers-inventory-table'
      rowSelection={rowSelection as any}
      columns={
        !isPreorder
          ? [...STORE_MODIFIER_COLUMNS, ...MODIFIER_SNOOZE_COLUMN]
          : STORE_MODIFIER_COLUMNS
      }
      dataSource={storeModifiers}
      rowKey='id'
    />
  )
}

const WrappText = styled.span`
  text-wrap: wrap;
  width: 100%;
  max-width: 250px;
`

export default ModifiersInventoryTable
